/**
 * Types for CreateFundraisingPaymentDrawer and FundraiserLayout components
 */

import { Dispatch, SetStateAction, RefObject } from 'react';
import { Merchant } from '../../../types/common';

// ============================================================================
// Custom Input Types
// ============================================================================

export interface CustomInput {
  id: string;
  title: string;
  label?: string;
  type:
    | 'SHORT_TEXT_INPUT'
    | 'LONG_TEXT_INPUT'
    | 'SINGLE_SELECTION_DROPDOWN'
    | 'MULTI_SELECTION_DROPDOWN';
  // API uses questionType: 'ATTENDEE' or 'PURCHASER'
  questionType?: 'ATTENDEE' | 'PURCHASER';
  // Legacy field - kept for backward compatibility
  inputType?: 'attendee' | 'purchaser' | 'fund' | 'global';
  required: boolean;
  options?: string[];
  value?: string;
  answers?: string[];
  global?: boolean;
  fundId?: string;
  fundItemId?: string; // 🆔 Unique ID linking custom input to its parent fund/cart item
  placeholder?: string;
  cartItemKey?: string;
}

// ============================================================================
// Fund Types
// ============================================================================

export type FrequencyType = 'One-Time' | 'Daily' | 'Weekly' | 'Monthly' | 'Annually';

export interface Fund {
  fundName: string;
  amount: number | string;
  fundraiser: string;
  formID: string;
  productGroup: string;
  fundraiserHasSponsershipGroups: boolean;
  product: string | ProductInfo;
  customInputs: CustomInput[];
  quantity: number;
  frequency: FrequencyType;
  nextChargeDate: Date | string | null;
  endChargeDate: Date | string | null;
  chargeTime: string | null;
  // Optional fields for backward compatibility with backend responses
  productID?: string;
  productName?: string;
  cartItemKey?: string;
  fundItemId?: string | null; // 🆔 Unique ID linking fund to its custom inputs
}

// Backend fund type - used when processing funds for API calls
// More permissive than Fund to allow for runtime modifications
export interface BackendFund {
  productID?: string;
  productName?: string;
  product?: ProductInfo | string;
  fundraiserHasSponsershipGroups?: boolean;
  chargeTime?: string | null;
  amount: number | string;
  frequency: string;
  cartItemKey?: string;
  fundItemId?: string | null; // 🆔 Unique ID linking fund to its custom inputs
  fundName: string;
  fundraiser: string;
  formID: string;
  customInputs?: CustomInput[];
  quantity: number;
  nextChargeDate?: Date | string | null;
  endChargeDate?: Date | string | null;
  [key: string]: unknown;
}

export interface ProductInfo {
  productID: string;
  productName: string;
  productPrice: number;
}

export interface ProductGroup {
  productGroupName: string;
  products: ProductInfo[];
}

// ============================================================================
// Form/Fundraiser Data Types
// ============================================================================

export interface DefaultScheduledRange {
  enabled?: boolean;
  scheduleType?: 'immediate' | 'days' | 'weeks' | 'months' | 'years';
  startDateOffset?: string | number;
  endDateOffset?: string | number;
  scheduledTime?: string;
}

export interface FundraiserData {
  elementID: string;
  formID?: string;
  formName: string;
  formType: 'EVENT' | 'FUNDRAISER';
  fundraiserType?: 'EMBED_FORM' | 'CROWDFUNDING_FORM' | 'PRODUCT';
  funds?: string[];
  frequencies?: string[];
  productGroups?: ProductGroup[];
  // Frequency-specific product groups (take priority over generic productGroups)
  oneTimeProductGroups?: ProductGroup[] | null;
  dailyProductGroups?: ProductGroup[] | null;
  weeklyProductGroups?: ProductGroup[] | null;
  monthlyProductGroups?: ProductGroup[] | null;
  annuallyProductGroups?: ProductGroup[] | null;
  customInputs?: CustomInput[];
  fieldMappings?: FieldMapping[];
  showScheduleDonation?: boolean;
  defaultScheduledRange?: DefaultScheduledRange;
  // Dedication fields
  enableDedication?: boolean;
  dedicationTypes?: string[];
  enableDedicationMessage?: boolean;
  // Communication consent fields
  enableCommunicationConsent?: boolean;
  communicationConsentMessage?: string;
}

export interface FieldMapping {
  fieldName: string;
  mappedTo: string;
  fundName?: string;
}

// ============================================================================
// Form Data Types
// ============================================================================

export interface FundraisingFormData {
  // Basic payment info
  amount: number;
  coverTransactionFee: boolean;
  coverFees: boolean;
  feeCovered: boolean;
  paymentMethod: 'Card' | 'Bank' | string;
  paymentType?: string;

  // Bank payment details
  accountNumber: string;
  routingNumber: string;
  accountType: 'CHECKING' | 'SAVINGS' | string;

  // Payment state
  anonymous: boolean;
  cardToken: string | null;

  // Contact info
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  organizationName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  region?: string;
  zip?: string;
  country?: string;
  contactID?: string;
  contactSearch?: string;

  // Funds data
  funds: Fund[];

  // Legacy ticket support (kept for compatibility)
  tickets?: unknown[];

  // Form name
  formName: string;

  // Form mappings
  formToFormDataMap?: Record<string, FundraiserData>;
  formToTicketMapping?: Record<string, unknown[]>;

  // Custom inputs
  globalCustomInputs?: CustomInput[];
  purchaserCustomInputs?: CustomInput[];

  // Dedication fields (user input)
  dedicationType?: string;
  dedicationName?: string;
  dedicationMessage?: string;

  // Communication consent (user input)
  communicationConsent?: boolean;

  // Edit mode fields
  timestamp?: string | Date | null;
  customFeeAmount?: number | string;
  fees?: number;

  // Allow additional properties
  [key: string]: unknown;
}

// ============================================================================
// Transaction Types
// ============================================================================

export interface Transaction {
  transactionID: string;
  grossAmount: number | string;
  fees: number | string;
  coverFees: boolean | 'Yes' | 'No';
  paymentMethod: string;
  bankAccountType?: string;
  anonymous: boolean;
  email: string;
  name: string;
  phone?: string;
  organizationName?: string;
  address?: string;
  address2?: string;
  city?: string;
  region?: string;
  zip?: string;
  country?: string;
  timestamp?: string;
  formName?: string;
  funds?: Fund[];
  customInputs?: CustomInput[];
  accountNumber?: string;
  routingNumber?: string;
  importedTransaction?: boolean;
  timezone?: string;
  contactID?: string;
  state?: string;
  // Dedication fields
  dedicationType?: string;
  dedicationName?: string;
  dedicationMessage?: string;
  // Communication consent
  communicationConsent?: boolean;
}

// ============================================================================
// Payment Error Types
// ============================================================================

export interface PaymentError {
  message: string | string[];
  code?: string;
  show?: boolean;
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface SelectableFund {
  label: string;
  value: string;
  color?: string;
}

export interface Form {
  formID: string;
  formName: string;
  formType: string;
  [key: string]: any;
}

export interface CreateFundraisingPaymentDrawerProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  merchant: Merchant | null;
  selectableFunds?: SelectableFund[];
  selectableForms?: Form[];
  transaction?: Transaction;
  /** The subscription the edited transaction belongs to. An edit still shows the gift's stored fee (storedGiftFee). */
  linkedSubscription?: Record<string, unknown> | null;
  grabSelectableFunds?: (token?: string) => void;
  onTransactionCreated?: (transaction: Transaction) => void;
  onMultipleTransactionsCreated?: (transactions: Transaction[]) => void;
  updateData?: (error: unknown, updatedDonation: Transaction) => void;
  presentation?: 'drawer' | 'workspace';
  onWorkspaceBack?: () => void;
  setWorkspaceActions?: (actions: WorkspaceHeaderAction[] | null) => void;
}

export interface WorkspaceHeaderAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'ghost' | 'outline' | 'neutral' | 'primary' | 'danger';
  className?: string;
}

export interface FundraiserLayoutProps {
  presentation?: 'drawer' | 'workspace';

  // Amount and fees
  donationAmount: string;
  fees: number;
  feeCovered: boolean;
  setFeeCovered: Dispatch<SetStateAction<boolean>>;
  setFees: Dispatch<SetStateAction<number>>;

  // Form data
  formData: FundraisingFormData;
  setFormData: Dispatch<SetStateAction<FundraisingFormData>>;

  // Merchant
  merchant: Merchant | null;

  // Amount handlers
  handleCustomAmountChange: (value: string | number) => void;
  handleFeeCoverageChange: (covered: boolean, currentFees: number) => void;

  // Validation
  invalidFields: Set<string>;
  setInvalidFields: Dispatch<SetStateAction<Set<string>>>;
  handleFieldFocus: (fieldName: string) => void;
  isFieldInvalid?: (fieldId: string) => boolean;

  // Payment method refs
  stripeCardRef: RefObject<StripeCardRef | null>;
  finixCardRef: RefObject<FinixCardRef | null>;
  finixBankRef: RefObject<FinixBankRef | null>;

  // Payment method state
  paymentMethod: 'Card' | 'Bank';
  setPaymentMethod: Dispatch<SetStateAction<'Card' | 'Bank'>>;
  form: FinixForm | null;
  setForm: Dispatch<SetStateAction<FinixForm | null>>;
  setTokenID: Dispatch<SetStateAction<string>>;

  // Navigation
  isDrawerOpen: boolean;
  currentStep: FormStep;
  setCurrentStep: Dispatch<SetStateAction<FormStep>>;
  handleBack: () => void;
  handleContinue: () => void;

  // State flags
  isDisabled: boolean;
  isSubmitting: boolean;
  isEditMode: boolean;

  // Transaction (edit mode)
  transaction?: Transaction;
  linkedSubscription?: Record<string, unknown> | null;

  // Delete handler (prototype - backend implementation needed)
  onDeletePayment?: () => void;

  // Optional
  paymentError?: PaymentError;
  calculateFees?: (amount: number, merchant: Merchant, paymentMethod: string) => number;
  isToday?: (date: Date | string | null) => boolean;
  jwtToken?: string;

  // Contact selection state (moved from internal state for validation access)
  selectedContact: Contact | null;
  setSelectedContact: Dispatch<SetStateAction<Contact | null>>;
  isNewContact: boolean;
  setIsNewContact: Dispatch<SetStateAction<boolean>>;
}

// ============================================================================
// Form Step Type
// ============================================================================

export type FormStep =
  | 'form_information'
  | 'dedication_information'
  | 'custom_input_information'
  | 'contact_information'
  | 'payment_information';

// ============================================================================
// Contact Types
// ============================================================================

export interface Contact {
  contactID?: string;
  id?: string;
  fullName?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  emails?: ContactEmail[];
  phones?: ContactPhone[];
  addresses?: ContactAddress[];
}

export interface ContactEmail {
  // Post-Phase-5 shape from backend uses `email`; legacy/form-state still uses `value`.
  email?: string;
  value?: string;
  is_primary: boolean;
  deleted?: boolean;
  // Additional Phase 5 mirror fields from ContactEmailsTable.
  id?: string;
  emailMerchantKey?: string;
  is_verified?: boolean;
  stripeCustomerID?: string;
  finixBuyerID?: string;
  merchantName?: string;
  merchantID?: string;
  clerkUserID?: string;
  added_at?: string;
  updated_at?: string;
}

export interface ContactPhone {
  value: string;
  is_primary: boolean;
  deleted?: boolean;
}

export interface ContactAddress {
  address1?: string;
  address2?: string;
  line1?: string;
  line2?: string;
  city?: string;
  region?: string;
  zip?: string;
  postal?: string;
  country?: string;
  is_primary?: boolean;
  deleted?: boolean;
}

// ============================================================================
// Helper function types
// ============================================================================

export type CalculateFeesFunction = (
  amount: number,
  merchant: Merchant,
  paymentMethod: string,
) => number;

export type CalculateTotalFunction = (
  baseAmount: number,
  feeAmount: number,
  isFeeCovered: boolean,
) => number;

// ============================================================================
// Payment Form Types (Finix)
// ============================================================================

export interface FinixFormResponse {
  data: {
    id: string;
  };
}

export interface FinixForm {
  submit: (
    environment: string,
    applicationId: string,
    callback: (err: unknown, res: FinixFormResponse) => void,
  ) => void;
}

// ============================================================================
// Payment Ref Types
// ============================================================================

export interface StripeCardRef {
  handleSubmit: () => Promise<{
    success: boolean;
    paymentMethod?: unknown;
    error?: unknown;
  }>;
}

export interface FinixCardRef {
  checkIframeErrors: () => Promise<string[]>;
}

export interface FinixBankRef {
  checkValidity: () => Promise<string[]>;
}

// ============================================================================
// Step Navigation Props
// ============================================================================

export interface StepNavigationProps {
  currentStep: FormStep;
  onBack: () => void;
  onContinue: () => void;
  paymentType?: string;
  isEditMode?: boolean;
  selectedContact?: Contact | null;
  isNewContact?: boolean;
}

export interface SectionHeaderProps {
  title: string;
  hasErrors?: boolean;
  onClick?: () => void;
}

// ============================================================================
// Address Autocomplete Types (Mapbox)
// ============================================================================

export interface AddressFeatureProperties {
  address_line1?: string;
  address_line2?: string;
  city?: string;
  place?: string;
  state_code?: string;
  state?: string;
  region_code?: string;
  postcode?: string;
  country_code?: string;
}

export interface AddressFeature {
  properties: AddressFeatureProperties;
}

export interface AddressAutocompleteEvent {
  features?: AddressFeature[];
}

// ============================================================================
// Iframe State Types (Finix)
// ============================================================================

export interface IframeState {
  errors?: string[];
  complete?: boolean;
}

// ============================================================================
// Country Type
// ============================================================================

export interface CountryOption {
  value: string;
  label: string;
}

// ============================================================================
// Date Picker Types (Dayjs compatibility)
// ============================================================================

export interface DayjsLike {
  $d?: Date;
  format?: (format: string) => string;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Resolves the correct product groups for a given frequency.
 * Uses the form's frequencies list to determine if per-frequency data should be used.
 * If the frequency is selected (in the frequencies list), use per-frequency product groups.
 * If the frequency is not selected, fall back to the legacy productGroups.
 */
export function getProductGroupsForFrequency(
  fundraiserData: FundraiserData | undefined,
  frequency: FrequencyType,
): ProductGroup[] | undefined {
  if (!fundraiserData) return undefined;

  // Map dashboard frequency names to backend format used in the frequencies array
  const frequencyToBackendMap: Record<FrequencyType, string> = {
    'One-Time': 'ONE_TIME',
    Daily: 'DAILY',
    Weekly: 'WEEKLY',
    Monthly: 'MONTHLY',
    Annually: 'ANNUALLY',
  };

  const frequencyGroupMap: Record<FrequencyType, ProductGroup[] | null | undefined> = {
    'One-Time': fundraiserData.oneTimeProductGroups,
    Daily: fundraiserData.dailyProductGroups,
    Weekly: fundraiserData.weeklyProductGroups,
    Monthly: fundraiserData.monthlyProductGroups,
    Annually: fundraiserData.annuallyProductGroups,
  };

  const backendFrequency = frequencyToBackendMap[frequency];
  const formFrequencies = fundraiserData.frequencies;

  // If the form has a frequencies list and this frequency is in it,
  // use the per-frequency product groups for that frequency
  if (Array.isArray(formFrequencies) && formFrequencies.includes(backendFrequency)) {
    const frequencySpecific = frequencyGroupMap[frequency];
    if (Array.isArray(frequencySpecific) && frequencySpecific.length > 0) {
      return frequencySpecific;
    }
  }

  // Fall back to legacy productGroups when:
  // - The form has no frequencies list (legacy forms)
  // - The frequency is not in the form's selected frequencies
  // - The per-frequency array is empty for a selected frequency
  return fundraiserData.productGroups ?? undefined;
}
